import { Palette } from "lucide-react";
import { ColorField } from "@/components/admin/companyProfile/ColorField";
import { inputClass, labelClass, textareaClass } from "@/components/admin/companyProfile/styles";
import type { BrandFormState } from "./brandForm";

interface BrandKitFieldsProps {
  form: BrandFormState;
  setField: <K extends keyof BrandFormState>(key: K, value: BrandFormState[K]) => void;
  readOnly: boolean;
}

/** The branding-kit field groups: palette, fonts, tone, prose guidance. */
export function BrandKitFields({ form, setField, readOnly }: Readonly<BrandKitFieldsProps>) {
  return (
    <>
      <div>
        <h4 className="text-sm font-medium text-content-secondary mb-2">Brand palette</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ColorField
            id="brand-primary"
            label="Primary"
            value={form.brand_primary}
            onChange={(v) => setField("brand_primary", v)}
          />
          <ColorField
            id="brand-secondary"
            label="Secondary"
            value={form.brand_secondary}
            onChange={(v) => setField("brand_secondary", v)}
          />
          <ColorField
            id="brand-background"
            label="Background"
            value={form.brand_background}
            onChange={(v) => setField("brand_background", v)}
          />
          <ColorField
            id="brand-text"
            label="Text"
            value={form.brand_text}
            onChange={(v) => setField("brand_text", v)}
          />
        </div>
        <div className="mt-3">
          <label htmlFor="brand-accents" className={labelClass}>
            Accent colors (comma-separated hex)
          </label>
          <input
            id="brand-accents"
            type="text"
            value={form.brand_accents}
            onChange={(e) => setField("brand_accents", e.target.value)}
            placeholder="#00EDFF, #D13C8D"
            disabled={readOnly}
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="brand-font-heading" className={labelClass}>
            Heading font
          </label>
          <input
            id="brand-font-heading"
            type="text"
            value={form.brand_font_heading}
            onChange={(e) => setField("brand_font_heading", e.target.value)}
            placeholder="Poppins"
            disabled={readOnly}
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="brand-font-body" className={labelClass}>
            Body font
          </label>
          <input
            id="brand-font-body"
            type="text"
            value={form.brand_font_body}
            onChange={(e) => setField("brand_font_body", e.target.value)}
            placeholder="Inter"
            disabled={readOnly}
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label htmlFor="brand-tone" className={labelClass}>
          Brand tone
        </label>
        <input
          id="brand-tone"
          type="text"
          value={form.brand_tone}
          onChange={(e) => setField("brand_tone", e.target.value)}
          placeholder="e.g. formal, confident, approachable"
          disabled={readOnly}
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor="brand-style" className={labelClass}>
          <Palette className="inline h-3.5 w-3.5 mr-1" />
          Additional guidance (prose)
        </label>
        <textarea
          id="brand-style"
          value={form.brand_style}
          onChange={(e) => setField("brand_style", e.target.value)}
          placeholder="e.g. Use generous whitespace. Logo top-left on exports…"
          rows={3}
          maxLength={2000}
          disabled={readOnly}
          className={textareaClass}
        />
      </div>
    </>
  );
}
